import * as Sentry from '@sentry/react';
import type { BrowserOptions } from '@sentry/react';
import { env } from './env';

/**
 * Initialize Sentry for error tracking.
 * Only initializes in production builds.
 */
export function initSentry(): void {
  // Only initialize in production
  if (!env.isProd) {
    return;
  }

  const dsn = env.sentryDsn;

  // Skip initialization if DSN is not provided
  if (!dsn) {
    console.warn('Sentry DSN not provided. Error tracking disabled.');
    return;
  }

  const release = env.appVersion;

  const options: BrowserOptions = {
    dsn,
    environment: 'production',
    ...(release && { release }),
    // Ignore network errors from ad blockers/content blockers
    // These are expected and don't affect error tracking functionality
    ignoreErrors: [
      'ERR_BLOCKED_BY_CLIENT',
      'ERR_BLOCKED_BY_CONTENT_BLOCKER',
      'NetworkError',
      // Ignore ResizeObserver errors (common browser quirk, not actionable)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Ignore extension errors
      'Non-Error promise rejection captured',
      'ChunkLoadError',
    ],
    // Configure which integrations to use
    integrations: [
      Sentry.browserTracingIntegration({
        // Enable routing instrumentation for TanStack Router
        enableInp: true, // Enable Interaction to Next Paint (INP) tracking
        enableLongTask: true, // Track long tasks that block the main thread
      }),
      Sentry.replayIntegration({
        // Session Replay configuration
        // Only capture replays for errors and slow sessions
        // Note: sessionSampleRate and errorSampleRate are configured at the top level
        // to avoid TypeScript issues with the current SDK version
        maskAllText: true, // Privacy: mask all text by default
        blockAllMedia: true, // Privacy: block all media (images, videos, iframes)
      }),
    ],
    // Performance monitoring
    tracesSampleRate: 0.2, // Capture 20% of transactions for performance monitoring
    // Reduce sample rate in production if volume is high
    // Adjust based on your traffic: 1.0 = 100%, 0.1 = 10%, etc.
    // Session Replay sampling rates
    // Note: These may need to be set at the top level depending on SDK version
    // sessionSampleRate: 0.1, // 10% of normal sessions for replay
    // errorSampleRate: 1.0, // 100% of sessions with errors for replay
    // Error sample rate defaults to 1.0 (100% of errors captured)
    // You can adjust with: errorSampleRate: 0.8, // 80% of errors
    // Configure beforeSend to filter or modify events before sending
    beforeSend(event, hint) {
      // Filter out non-actionable errors
      if (event.exception) {
        const error = hint.originalException;
        // Ignore specific error types that aren't useful
        if (error instanceof Error && error.message.includes('Non-Error promise rejection')) {
          // Don't send to Sentry
          // eslint-disable-next-line unicorn/no-null
          return null;
        }
      }
      return event;
    },
    // Configure breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        // Optionally filter out console.log breadcrumbs
        // eslint-disable-next-line unicorn/no-null
        return null;
      }
      return breadcrumb;
    },
    // Additional options
    maxBreadcrumbs: 100, // Maximum number of breadcrumbs per event (default: 100)
    sendDefaultPii: false, // Don't send PII by default (respect privacy)
  };

  Sentry.init(options);
}

/**
 * Set user context in Sentry when user logs in.
 * Call this after successful authentication.
 */
export function setSentryUser(userId: string, email?: string, username?: string): void {
  if (!env.isProd) {
    return;
  }

  Sentry.setUser({
    id: userId,
    email,
    username,
  });
}

/**
 * Clear user context in Sentry when user logs out.
 */
export function clearSentryUser(): void {
  if (!env.isProd) {
    return;
  }

  // eslint-disable-next-line unicorn/no-null
  Sentry.setUser(null);
}

/**
 * Capture an error to Sentry (production only)
 * Also logs to console in production for debugging
 * @param error - Error to capture
 * @param context - Additional context to include with the error
 */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
  // Always log to console (both dev and prod)
  const errorMessage = error instanceof Error ? error.message : String(error);
  const logLevel = (context?.logLevel as 'error' | 'warn') || 'error';

  if (logLevel === 'error') {
    console.error('[Sentry]', errorMessage, { error, context });
  } else {
    console.warn('[Sentry]', errorMessage, { error, context });
  }

  // Send to Sentry in production only
  if (!env.isProd) {
    return;
  }

  if (error instanceof Error) {
    Sentry.captureException(error, {
      contexts: context ? { custom: context } : undefined,
      level: logLevel === 'warn' ? 'warning' : 'error',
    });
  } else {
    Sentry.captureException(new Error(String(error)), {
      contexts: context ? { custom: context } : undefined,
      level: logLevel === 'warn' ? 'warning' : 'error',
    });
  }
}

/**
 * Add custom tags to Sentry scope.
 * Useful for filtering errors by feature, route, etc.
 */
export function setSentryTag(key: string, value: string): void {
  if (!env.isProd) {
    return;
  }

  Sentry.setTag(key, value);
}

/**
 * Add custom context data to Sentry scope.
 * Useful for adding additional debugging information.
 */
export function setSentryContext(name: string, context: Record<string, unknown>): void {
  if (!env.isProd) {
    return;
  }

  Sentry.setContext(name, context);
}
